import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { getFeedbacks, updateFeedback, deleteFeedback } from '../../services/feedbackService'
import withAdminAuth from './withAdminAuth'
import classes from './feedback_manager.module.css'

const TYPE_FILTERS = [
  { key: '', label: 'All types' },
  { key: 'suggestion', label: 'Suggestions' },
  { key: 'bug', label: 'Bug reports' }
]

const STATUS_FILTERS = [
  { key: '', label: 'All statuses' },
  { key: 'new', label: 'New' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'resolved', label: 'Resolved' }
]

const CATEGORY_LABELS = {
  price: 'Too expensive',
  content: 'No wallpapers they like',
  usability: 'Hard to use',
  battery: 'Battery / performance',
  other: 'Other suggestion',
  bug: 'Bug'
}

const PAGE_SIZE = 50

function formatDate (iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// "TW" -> "Taiwan (TW)"; falls back to the raw code for unknown values.
function countryName (code) {
  if (!code) return ''
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase())
    return name && name !== code.toUpperCase() ? `${name} (${code.toUpperCase()})` : code.toUpperCase()
  } catch (err) {
    return code.toUpperCase()
  }
}

function FeedbackRow ({ item, onChanged }) {
  const [note, setNote] = useState(item.adminNote || '')
  const [busy, setBusy] = useState(false)

  const setStatus = async (status) => {
    setBusy(true)
    try {
      await updateFeedback(item.id, { status })
      onChanged()
    } catch (err) {
      console.error(err)
      window.alert('Failed to update status')
    } finally {
      setBusy(false)
    }
  }

  const saveNote = async () => {
    setBusy(true)
    try {
      await updateFeedback(item.id, { adminNote: note })
      onChanged()
    } catch (err) {
      console.error(err)
      window.alert('Failed to save note')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!window.confirm('Delete this feedback (and its screenshots)?')) return
    setBusy(true)
    try {
      await deleteFeedback(item.id)
      onChanged()
    } catch (err) {
      console.error(err)
      window.alert('Failed to delete')
      setBusy(false)
    }
  }

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <span className={item.type === 'bug' ? classes.badgeBug : classes.badgeSuggestion}>
          {item.type === 'bug' ? 'BUG' : 'SUGGESTION'}
        </span>
        <span className={classes.category}>{CATEGORY_LABELS[item.category] || item.category}</span>
        {(item.ipCountry || item.localeCountry) && (
          <span className={classes.country}>
            🌍 {countryName(item.ipCountry || item.localeCountry)}
          </span>
        )}
        <span className={classes.date}>{formatDate(item.createdAt)}</span>
        <select
          className={classes.statusSelect}
          value={item.status || 'new'}
          disabled={busy}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="new">new</option>
          <option value="reviewed">reviewed</option>
          <option value="resolved">resolved</option>
        </select>
        <button className={classes.deleteBtn} disabled={busy} onClick={remove}>Delete</button>
      </div>

      {item.message && <div className={classes.message}>{item.message}</div>}

      {item.screenshots && item.screenshots.length > 0 && (
        <div className={classes.screenshots}>
          {item.screenshots.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer">
              <img className={classes.thumb} src={url} alt="screenshot" loading="lazy" />
            </a>
          ))}
        </div>
      )}

      <div className={classes.meta}>
        {item.type === 'bug' && (
          <span>👤 {item.name || 'user'}{item.email ? ` · ${item.email}` : ''}</span>
        )}
        <span>📱 {item.manufacturer} {item.model} (Android {item.release}, SDK {item.sdk})</span>
        <span>🌍 IP: {countryName(item.ipCountry) || '—'} · locale: {countryName(item.localeCountry) || '—'} · {item.language || '—'}</span>
        <span>📦 v{item.appVersion} ({item.versionCode})</span>
        <span title={item.deviceId}>🔑 {String(item.deviceId || '').slice(0, 8)}…</span>
      </div>

      <div className={classes.noteRow}>
        <input
          className={classes.noteInput}
          value={note}
          placeholder="Admin note…"
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          className={classes.noteSaveBtn}
          disabled={busy || note === (item.adminNote || '')}
          onClick={saveNote}
        >
          Save note
        </button>
      </div>
    </div>
  )
}

FeedbackRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    category: PropTypes.string,
    message: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    screenshots: PropTypes.arrayOf(PropTypes.string),
    deviceId: PropTypes.string,
    appVersion: PropTypes.string,
    versionCode: PropTypes.number,
    manufacturer: PropTypes.string,
    model: PropTypes.string,
    release: PropTypes.string,
    sdk: PropTypes.string,
    localeCountry: PropTypes.string,
    ipCountry: PropTypes.string,
    language: PropTypes.string,
    status: PropTypes.string,
    adminNote: PropTypes.string,
    createdAt: PropTypes.string
  }).isRequired,
  onChanged: PropTypes.func.isRequired
}

function FeedbackManager () {
  const [data, setData] = useState(null)
  const [page, setPage] = useState(0)
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await getFeedbacks(page, PAGE_SIZE, type, status))
    } catch (err) {
      // Stale-key 401s are recovered globally in axiosConfig.js (re-prompt + retry);
      // reaching here means the retry also failed or it's another error.
      console.error(err)
      window.alert('Failed to load feedback (check the admin API key)')
    } finally {
      setLoading(false)
    }
  }, [page, type, status])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className={classes.page}>
      <div className={classes.inner}>
        <h1 className={classes.title}>User Feedback</h1>
        <p className={classes.subtitle}>
          {data ? `${data.totalItems} total` : 'Loading…'}
        </p>

        <div className={classes.toolbar}>
          <div className={classes.tabs}>
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.key}
                className={type === f.key ? `${classes.tab} ${classes.tabActive}` : classes.tab}
                onClick={() => { setType(f.key); setPage(0) }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className={classes.tabs}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                className={status === f.key ? `${classes.tab} ${classes.tabActive}` : classes.tab}
                onClick={() => { setStatus(f.key); setPage(0) }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className={classes.subtitle}>Loading…</p>}
        {!loading && data && data.items.length === 0 && (
          <p className={classes.subtitle}>No feedback yet.</p>
        )}
        {!loading && data && data.items.map((item) => (
          <FeedbackRow key={item.id} item={item} onChanged={load} />
        ))}

        {data && data.totalPages > 1 && (
          <div className={classes.pager}>
            <button
              className={classes.tab}
              disabled={page <= 0}
              onClick={() => setPage(page - 1)}
            >
              ← Prev
            </button>
            <span className={classes.pageInfo}>
              {page + 1} / {data.totalPages}
            </span>
            <button
              className={classes.tab}
              disabled={!data.hasMore}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default withAdminAuth(FeedbackManager)
